import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import DashBoardNavBar from '../components/DashBoardNavBar.tsx';
import Navbar from '../components/Navbar.tsx';
import { Particles } from '../components/Particles.tsx';
import Snackbar from '@mui/material/Snackbar';
import CryptoJS from 'crypto-js';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Zoom, Stack, Typography, Box } from '@mui/material';
import { styled } from '@mui/system';
import { useNavigate } from "react-router-dom";
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const SECRET_KEY = 'BAG_v#DbA3&l:<0eYs}2dICN_/3d1QOY';

// --- STYLED COMPONENTS ---

const encryptData = (data: string) => CryptoJS.AES.encrypt(data, SECRET_KEY).toString();

const decryptData = (encryptedData: string) => {
  const bytes = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY);
  return bytes.toString(CryptoJS.enc.Utf8);
};

const AnimatedDialog = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode }) => ({
  '& .MuiPaper-root': {
    borderRadius: '24px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    background: 'linear-gradient(45deg, #0f0c29 10%, #302b63 50%, #24243e 90%)',
    backgroundSize: '200% 200%',
    animation: 'backgroundMove 12s ease infinite',
    color: 'white',
    backdropFilter: 'blur(15px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.5)',
  },
  '@keyframes backgroundMove': {
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' },
  }
}));

const ShineButton = styled(Button)(({ theme }) => ({
  fontSize: '24px',
  padding: '20px',
  cursor: 'pointer',
  position: 'relative',
  background: 'linear-gradient(45deg, #ff6b6b, #f06595, #d63b6f)',
  color: 'white',
  borderRadius: '12px',
  textTransform: 'uppercase',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)',
  },
  '&:active': {
    transform: 'scale(0.98)',
  },
}));

// --- MAIN COMPONENT ---

const Clicker: React.FC = () => {
  const token = Cookies.get('token');
  const isAuthenticated = !!token;
  const navigate = useNavigate();
  const darkMode = true; 

  const [score, setScore] = useState<number>(0);
  const [sessionScore, setSessionScore] = useState<number>(0); 
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [gameStarted, setGameStarted] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(true);
  const [openEndDialog, setOpenEndDialog] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);
  const [periodRemaining, setPeriodRemaining] = useState<number | null>(null);
  const [showTimeTooltip, setShowTimeTooltip] = useState<boolean>(false);

  const [helpArrowsVisible, setHelpArrowsVisible] = useState<boolean>(false);
  const [helpSnackbarMessage, setHelpSnackbarMessage] = useState<string>("");
  const [helpSnackbarOpen, setHelpSnackbarOpen] = useState<boolean>(false);

  const formatTime = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const fetchPeriodTime = () => {
    if (isAuthenticated) {
      fetch(`${process.env.REACT_APP_API_URL}/v1/user/getperiodtime`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(res => res.json())
      .then(data => {
        if (data.time) {
          const periodEnd = new Date(new Date(data.time).getTime() + 24 * 60 * 60 * 1000);
          const interval = setInterval(() => {
            const now = new Date();
            const diff = periodEnd.getTime() - now.getTime();
            setPeriodRemaining(diff > 0 ? Math.floor(diff / 1000) : 0);
          }, 1000);
          return () => clearInterval(interval);
        }
      })
      .catch(err => console.error("Error refreshing time:", err));
    }
  };

  const handleAwesomeClick = () => {
    setOpenEndDialog(false);
    
    if (!isAuthenticated) {
      // Csak nullázzuk a pontszámot, nincs localStorage mentés
      setScore(0);
    } else {
      // Ha bejelentkezett, frissítjük a hátralévő időt
      fetchPeriodTime();
    }
  };

  useEffect(() => {
    fetchPeriodTime();
  }, [isAuthenticated, token]);

  useEffect(() => {
    if (isAuthenticated) {
      fetch(`${process.env.REACT_APP_API_URL}/v1/user/score`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(res => res.json())
      .then(data => setScore(data.score || 0))
      .catch(() => setScore(0));
    } else {
      // Nem olvasunk be localStorage-t, a vendég mindig nulláról indul
      setScore(0);
    }
  }, [isAuthenticated, token]);

  useEffect(() => {
    if (!openDialog) checkUserActiveStatus();
  }, [openDialog]);

  const checkUserActiveStatus = () => {
    if (isAuthenticated) {
      fetch(`${process.env.REACT_APP_API_URL}/v1/user/active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(res => res.json())
      .then(data => setIsButtonDisabled(data.daily_reward_active !== 'active'))
      .catch(() => setIsButtonDisabled(true));
    } else {
      setIsButtonDisabled(false);
    }
  };

  useEffect(() => {
    if (timeLeft !== null && timeLeft > 0) {
      const timer = setInterval(() => setTimeLeft(prev => (prev !== null ? prev - 1 : null)), 1000);
      return () => clearInterval(timer);
    } else if (timeLeft === 0) {
      setIsButtonDisabled(true);
      setOpenEndDialog(true);
      setGameStarted(false);
      setTimeLeft(null);
      checkUserActiveStatus();
    }
  }, [timeLeft, isAuthenticated]);

  const handleClick = () => {
    if (!gameStarted) {
      setGameStarted(true);
      setTimeLeft(60);
      setSessionScore(0);
      // Garantáljuk, hogy a játék elején is nullázódik, ha vendégről van szó
      if (!isAuthenticated) {
        setScore(0);
      }
    }
    setSessionScore(prev => prev + 1);
    setScore(prev => {
      const newScore = prev + 1;
      if (isAuthenticated) {
        fetch(`${process.env.REACT_APP_API_URL}/v1/user/updateScore`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ score: newScore }),
        });
      }
      // Nem mentjük a vendég score-ját a localStorage-ba
      return newScore;
    });
  };

  const handleHelpClick = () => {
    setHelpArrowsVisible(true);
    setHelpSnackbarMessage("Click that button.");
    setHelpSnackbarOpen(true);
    setTimeout(() => setHelpSnackbarMessage("Yeah that is a Button..."), 3000);
    setTimeout(() => {
      setHelpSnackbarOpen(false);
      setHelpArrowsVisible(false);
    }, 5000);
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', textAlign: 'center' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: -1 }}>
        <Particles />
      </div>
      
      {isAuthenticated ? <DashBoardNavBar /> : <Navbar />}

      <div style={{ position: 'absolute', top: '40px', right: '40px', zIndex: 10 }}>
        <Button variant="contained" color="secondary" onClick={() => navigate('/shop')}>
          Shop
        </Button>
      </div>

      {isAuthenticated && (
        <Box sx={{ 
          position: 'absolute', 
          top: '10px', 
          left: '20px', 
          textAlign: 'left', 
          color: darkMode ? '#00ce03' : '#1a1a1a' 
        }}>
          <div 
            onMouseEnter={() => setShowTimeTooltip(true)} 
            onMouseLeave={() => setShowTimeTooltip(false)}
            style={{ fontSize: '20px', cursor: 'default', fontWeight: 'bold' }}
          >
            Time: {periodRemaining !== null ? (periodRemaining === 0 ? "Ready!" : formatTime(periodRemaining)) : "Loading..."}
          </div>
          {showTimeTooltip && (
            <Typography variant="caption" sx={{ display: 'block', bgcolor: 'rgba(0,0,0,0.7)', color: 'white', p: 0.5, borderRadius: 1 }}>
              That's how much time you have left to collect points.
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ mt: 10, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="h4" sx={{ color: 'white', mb: 2, textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
          {timeLeft !== null ? `Time Left: ${timeLeft}s` : 'Press to Start'}
        </Typography>

        <div style={{ position: 'relative' }}>
          <ShineButton onClick={handleClick} disabled={isButtonDisabled}>
            {score}
          </ShineButton>

          {helpArrowsVisible && (
            <>
              <ArrowDownwardIcon style={{ position: 'absolute', top: '-10px', left: '-100px', fontSize: '70px', color: 'red', transform: 'rotate(280deg)' }} />
              <ArrowDownwardIcon style={{ position: 'absolute', top: '-45px', right: '-100px', fontSize: '90px', color: 'red', transform: 'rotate(70deg)' }} />
            </>
          )}
        </div>
      </Box>

      <Snackbar open={helpSnackbarOpen} message={helpSnackbarMessage} />

      <AnimatedDialog 
        open={openDialog} 
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') setOpenDialog(false);
        }}
        darkMode={true}
        TransitionComponent={Zoom}
        transitionDuration={500}
        disableEscapeKeyDown 
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', pt: 3, fontSize: '1.8rem' }}>
          Welcome to Clicker Game!
        </DialogTitle>
        <DialogContent>
          <Box sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
            <Stack spacing={2}>
              <Typography variant="body1">Click the button as many times as possible before time runs out.</Typography>
              <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)', p: 2, borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)' }}>
                <Typography variant="subtitle2" sx={{ color: 'gold', fontWeight: 'bold', mb: 1 }}>Rules:</Typography>
                <Typography variant="body2">• You have <strong>60 seconds</strong> to earn score points.</Typography>
                {isAuthenticated ? (
                  <>
                    <Typography variant="body2">• You can only earn score points in a <strong>24-hour period</strong>.</Typography>
                    <Typography variant="body2">• You can transfer your score into tokens at the end of the game.</Typography>
                    <Typography variant="body2" sx={{ mt: 1, color: '#ff6b6b', fontWeight: 'bold' }}>
                      WARNING: Starting will trigger your 24h period!
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body2">• The score is lost after one game.</Typography>
                    <Typography variant="body2" sx={{ mt: 1, color: 'gold' }}>
                      <strong>Score Save:</strong> Sign in to save your score and be the #1 player!
                    </Typography>
                  </>
                )}
              </Box>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 4, pb: 3 }}>
          <Button onClick={() => navigate(-1)} sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none' }}>Back</Button>
          <Button 
            onClick={() => setOpenDialog(false)} 
            variant="contained" 
            sx={{ 
              background: 'linear-gradient(45deg, #ff6b6b, #d63b6f)', 
              borderRadius: '12px',
              fontWeight: 'bold',
              px: 3
            }}
          >
            Let's Start
          </Button>
        </DialogActions>
      </AnimatedDialog>

      <AnimatedDialog 
        open={openEndDialog} 
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') handleAwesomeClick();
        }}
        darkMode={true}
        TransitionComponent={Zoom}
        transitionDuration={500}
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', pt: 3, fontSize: '1.8rem', color: 'gold' }}>
          🎉 Congratulations!
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          <Typography variant="h5" sx={{ mb: 2 }}>You've finished the round!</Typography>
          <Box sx={{ bgcolor: 'rgba(255,255,255,0.1)', p: 3, borderRadius: '20px', border: '2px dashed gold' }}>
            <Typography variant="h6">Points collected now:</Typography>
            <Typography variant="h2" sx={{ fontWeight: 'bold', color: 'gold' }}>{sessionScore}</Typography>
          </Box>
          <Typography variant="body1" sx={{ mt: 3, color: 'rgba(255,255,255,0.8)' }}>
            {isAuthenticated 
              ? "Your total score has been updated. Check back in 24 hours!" 
              : "Login to save points to your account!"}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button 
            onClick={handleAwesomeClick} 
            variant="contained"
            sx={{ bgcolor: 'white', color: 'black', fontWeight: 'bold', borderRadius: '10px', px: 5, '&:hover': { bgcolor: '#eee' } }}
          >
            Awesome!
          </Button>
        </DialogActions>
      </AnimatedDialog>

      <Button onClick={handleHelpClick} variant="contained" sx={{ position: 'fixed', bottom: 20, right: 20, bgcolor: '#ff4081', borderRadius: '50px' }}>
        Help
      </Button>
    </div>
  );
};

export default Clicker;